import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { showToast } from "./toast";
import type { WheelCategoryMeta, WheelSession } from "./types";
import type {
  WheelDeclinedData,
  WheelEndedData,
  WheelInviteData,
  WheelJoinedData,
  WheelReactionData,
  WheelResultData,
} from "./chatSocket";

// Framework-agnostic Couple Wheel data layer, same shape as gameApi.ts -
// the server response fully overwrites appState.wheelSession on every call,
// no optimistic merge (same reasoning as gameApi.ts: the server is the only
// thing that ever picks a result, so there's nothing useful to predict
// locally ahead of its response).

export type WheelResult<T extends object = { session?: never }> = ({ ok: true } & T) | { ok: false; error?: string };

export async function inviteWheelApi(
  params: { recipientEmail?: string; category?: string },
): Promise<WheelResult<{ session: WheelSession }>> {
  const res = await apiFetch("/api/couple-wheel/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.wheelSession = data.session as WheelSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function acceptWheelApi(sessionId: string): Promise<WheelResult<{ session: WheelSession }>> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/accept`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.wheelIncomingInvite = null;
  appState.wheelSession = data.session as WheelSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function declineWheelApi(sessionId: string): Promise<WheelResult> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/decline`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.wheelIncomingInvite = null;
  notifyStateChange();
  return { ok: true };
}

export async function spinWheelApi(sessionId: string): Promise<WheelResult<{ session: WheelSession }>> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/spin`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  // A 409 spin_in_progress still carries the current authoritative session
  // (see server.js) - apply it so this caller's own UI reflects the spin
  // that actually won instead of silently doing nothing.
  if (data?.session) {
    appState.wheelSession = data.session as WheelSession;
    notifyStateChange();
  }
  if (!res.ok) return { ok: false, error: data.message || data.error };

  return { ok: true, session: data.session };
}

export async function toggleWheelFavoriteApi(sessionId: string, resultId: string): Promise<WheelResult<{ session: WheelSession }>> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/favorite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resultId }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.wheelSession = data.session as WheelSession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function reactWheelApi(sessionId: string, emoji: string): Promise<WheelResult> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

export async function leaveWheelApi(sessionId: string): Promise<WheelResult> {
  const res = await apiFetch(`/api/couple-wheel/${sessionId}/leave`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.wheelSession = null;
  notifyStateChange();
  return { ok: true };
}

// Read-only lookup of this user's current session state, straight from the
// server (not appState) - used by CoupleWheelPage.tsx's reconnect-resync
// effect, same reasoning as gameApi.ts's fetchGameSessionApi.
export async function fetchWheelSessionApi(): Promise<WheelSession | null> {
  const res = await apiFetch("/api/couple-wheel/session");
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return (data.session as WheelSession | null) ?? null;
}

// Category/item metadata rarely/never changes at runtime - fetched once and
// reused (module-level cache, not appState), same reasoning as
// gameApi.ts's fetchGameDecksApi/gameDecksCache.
let wheelCategoriesCache: Promise<WheelCategoryMeta[] | null> | null = null;

export async function fetchWheelCategoriesApi(): Promise<WheelCategoryMeta[] | null> {
  if (!wheelCategoriesCache) {
    wheelCategoriesCache = (async () => {
      const res = await apiFetch("/api/couple-wheel/categories");
      if (res.status === 401) {
        requestLock("unauthorized");
        return null;
      }
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      return (data?.categories as WheelCategoryMeta[] | undefined) ?? null;
    })();
  }
  const result = await wheelCategoriesCache;
  if (!result) wheelCategoriesCache = null; // allow a retry on the next call instead of caching a failure forever
  return result;
}

// --- Apply incoming WS pushes to appState (called from the socket handler
// registry CoupleWheelPage.tsx sets up via setWheelSocketHandlers) ---

export function applyWheelInvite(data: WheelInviteData): void {
  // Ignore a stray invite if this client is somehow already in a session
  // (the server itself already rejects double-invites via 409, but a
  // client can still race the push with its own local state).
  if (appState.wheelSession) return;
  appState.wheelIncomingInvite = data;
  notifyStateChange();
}

export function applyWheelJoined(data: WheelJoinedData): void {
  if (!appState.wheelSession || appState.wheelSession.id !== data.sessionId) return;
  appState.wheelSession = { ...appState.wheelSession, status: "active" };
  notifyStateChange();
}

export function applyWheelDeclined(data: WheelDeclinedData): void {
  if (!appState.wheelSession || appState.wheelSession.id !== data.sessionId) return;
  showToast(`${appState.wheelSession.peerName} declined your wheel invite.`, "info");
  appState.wheelSession = null;
  notifyStateChange();
}

export function applyWheelResult(data: WheelResultData): void {
  if (!appState.wheelSession || appState.wheelSession.id !== data.sessionId) return;
  appState.wheelSession = {
    ...appState.wheelSession,
    spinState: "result",
    currentResultId: data.resultId,
    spinNumber: data.spinNumber,
    history: [data.resultId, ...appState.wheelSession.history].slice(0, 20),
  };
  notifyStateChange();
}

export function applyWheelReaction(data: WheelReactionData): void {
  appState.wheelReactions = [...appState.wheelReactions, { id: `${data.sessionId}-${Date.now()}`, emoji: data.emoji, fromUserId: data.fromUserId }];
  notifyStateChange();
}

export function applyWheelEnded(data: WheelEndedData): void {
  if (!appState.wheelSession || appState.wheelSession.id !== data.sessionId) return;
  if (data.reason === "left") showToast(`${appState.wheelSession.peerName} left the wheel.`, "info");
  else if (data.reason === "disconnected") showToast(`${appState.wheelSession.peerName} disconnected.`, "info");
  else if (data.reason === "expired") showToast("Your wheel invite expired.", "info");
  appState.wheelSession = null;
  notifyStateChange();
}
